import { ChangeEventHandler, KeyboardEventHandler, useState } from 'react';
import './Select.css';

type SelectProps = {
	placeholder: string;
	onSelect?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	onBlur?: ChangeEventHandler<HTMLDivElement>;
	value?: string | null;
	disabled?: boolean;
	onKeyDown?: KeyboardEventHandler<HTMLSelectElement>;
	isValid?: boolean;
	size?: 'small' | 'large' | null;
	icon?: boolean;
	list: any[];
	nullValue?: string;
	renderValue?: (e: any) => string;
	searchValue?: (query: string, array: any[]) => any | undefined;
	idField?: string;
	className?: string;
	large?: boolean;
};

export const Select = ({
	placeholder,
	value,
	onSelect,
	disabled,
	isValid,
	list,
	nullValue,
	renderValue,
	size,
	onBlur,
	searchValue,
	idField,
	className,
	large,
}: SelectProps): JSX.Element => {
	const [touched, setTouched] = useState(false);
	const [changed, setChanged] = useState(false);
	const [open, setOpen] = useState(false);
	const statusClassname = touched && typeof isValid === 'boolean' && !isValid ? 'error ' : '';
	const classes = [
		'dropdown-select',
		className,
		statusClassname,
		size && size,
		open && 'open',
		disabled && 'disabled',
		changed && 'changed',
		large && 'large',
	]
		.filter(Boolean)
		.join(' ');

	const toggleOpen = () => {
		setOpen((prev: boolean) => (disabled ? false : !prev));
	};

	const selectOption = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (onSelect) onSelect(event);
		toggleOpen();
		setChanged(true);
	};

	const getValue = (query: string) => {
		if (query) {
			if (searchValue) return searchValue(query, list);

			return list[parseInt(query)];
		}
	};

	const renderPlaceHolder = () => {
		if (value && value !== 'null') {
			if (renderValue && list) return renderValue(getValue(value));

			return value;
		} else if (value === 'null' && nullValue) {
			return nullValue;
		}

		return placeholder;
	};

	return (
		<div
			tabIndex={-1}
			className={classes}
			onFocus={() => {
				if (!touched) {
					setTouched(true);
				}
			}}
			onBlur={(e) => {
				if (onBlur) onBlur(e);
				setOpen(false);
			}}
		>
			<div className="dropdown-header" onClick={toggleOpen}>
				<div className="dropdown-header-placeholder">{renderPlaceHolder()}</div>
			</div>
			{open && (
				<div role="list" className="dropdown-list">
					{placeholder && <div className="list-item disabled">{placeholder}</div>}
					{list.map((item, id) => (
						<div
							key={id + JSON.stringify(item)}
							id={
								idField && item[idField] !== null ? item[idField] : typeof item === 'string' ? item : id
							}
							className="list-item"
							onClick={selectOption}
						>
							<span>{renderValue ? renderValue(item) : item}</span>
						</div>
					))}
					{nullValue && (
						<div key="null" id="null" className="list-item" onClick={selectOption}>
							<span>{nullValue}</span>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
